import { Autocomplete, TextField } from "@mui/material";
import { Axios } from "api/axios";
import CancelBackButton from "components/Buttons/CancelBackButton";
import SubmitButton from "components/Buttons/SubmitButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import PageContainer from "components/PageContainer/PageContainer";
import { autocompleteDropdownStyles } from "Constants/Style";
import { City, Country, State } from "country-state-city";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

// Common input style for Form.Control to match Autocomplete styling
const commonInputStyle = {
  fontSize: "0.8rem",
  minHeight: 38,
  padding: "0.375rem 0.75rem",
};

const commonLabelStyle = {
  fontSize: "0.8rem",
  marginBottom: 6,
  marginTop: 12,
};

interface FormValues {
  supplier_name: string;
  supplier_code: string;
  email: string;
  phone_no: string;
  address: string;
  country: any | null;
  state: any | null;
  city: any | null | "";
  pincode: string;
  gst_in: string;
  remark: string;
  contact_person_name: string;
  contact_person_phone_no: string;
  transporter_details: any;
}

export const SupplierMasterForm = () => {
  document.title = "Supplier Form";
  const { id } = useParams();
  const { state, pathname } = useLocation();
  const navigate = useNavigate();
  const [countries, setCountries] = useState<any>([]);
  const [states, setStates] = useState<any>([]);
  const [cities, setCities] = useState<any>([]);
  const [formLoding, setFormLoading] = useState<boolean>(false);

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      supplier_name: state ? state?.supplier_name : null,
      supplier_code: state ? state?.supplier_code : null,
      email: state ? state?.email : null,
      phone_no: state ? state?.phone_no : null,
      address: state ? state?.address : "",
      city: state ? { name: state?.city } : null,
      state: null,
      country: state ? { name: state?.country } : null,
      pincode: state ? state?.pincode : "",
      gst_in: state ? state?.gst_in : "",
      remark: state ? state?.remark : "",
      contact_person_name: state ? state?.contact_person_name : null,
      contact_person_phone_no: state ? state?.contact_person_phone_no : null,
      transporter_details: [
        {
          transporter_name:
            state?.transporter_details?.[0]?.transporter_name || "",
          transporter_contact_person_name:
            state?.transporter_details?.[0]?.transporter_contact_person_name ||
            "",
          transporter_contact_person_phone_no:
            state?.transporter_details?.[0]
              ?.transporter_contact_person_phone_no || "",
        },
        {
          transporter_name:
            state?.transporter_details?.[1]?.transporter_name || "",
          transporter_contact_person_name:
            state?.transporter_details?.[1]?.transporter_contact_person_name ||
            "",
          transporter_contact_person_phone_no:
            state?.transporter_details?.[1]
              ?.transporter_contact_person_phone_no || "",
        },
      ],
    },
    validationSchema: Yup.object({
      supplier_name: Yup.string().required("Supplier name is required"),
      supplier_code: Yup.string().required("Supplier code is required"),
      email: Yup.string()
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email format")
        .required("Email is required"),
      phone_no: Yup.string()
        .max(10, "Invalid contact number")
        .required("Contact number is required"),
      address: Yup.string().required("Address is required"),
      country: Yup.mixed().required("Country is required"),
      state: Yup.mixed().required("State is required"),
      city: Yup.mixed().required("City is required"),
      pincode: Yup.string()
        .required("Pincode is required")
        .matches(/^[1-9][0-9]{5}$/, "Enter a valid 6-digit pincode"),
      gst_in: Yup.string()
        .matches(
          /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
          "Invalid GSTIN format (27ABCDE1234F1Z5)"
        )
        .required("GSTIN is required"),
      contact_person_name: Yup.string().required(
        "Contact person name is required"
      ),
      contact_person_phone_no: Yup.string()
        .max(10, "Invalid contact number")
        .required("Contact number is required"),
      transporter_details: Yup.array().of(
        Yup.object().shape({
          transporter_name: Yup.string().nullable(),
          transporter_contact_person_name: Yup.string().nullable(),
          transporter_contact_person_phone_no: Yup.string()
            .nullable()
            .matches(/^[0-9]{10}$/, "Invalid phone number"),
        })
      ),
    }),
    onSubmit: async (values) => {
      // const cleanedTransporters = values.transporter_details.filter(
      //     (t: any) => t.transporter_name || t.transporter_contact_person_name || t.transporter_contact_person_phone_no
      // );
      const payload = {
        ...values,
        country: values?.country?.name,
        state: values?.state?.name,
        city: values?.city?.name,
        transporter_details: values?.transporter_details,
        // transporter_details: cleanedTransporters
      };

      try {
        setFormLoading(true);
        if (id) {
          await Axios.put(
            `/master/supplier/update/${state?.supplier_id}/`,
            payload
          );
          toast.success("Supplier Updated Successfully");
        } else {
          await Axios.post("/master/supplier/create", payload);
          toast.success("Supplier Created Successfully");
        }
        // setTimeout(() => {
        navigate("/supplier-list", { state: { clearState: !id } });
        // }, 1000);
      } catch (err: any) {
        const backendMessage =
          err?.response?.data?.error || err?.response?.data?.message;
        if (backendMessage) {
          toast.error(backendMessage);
        } else {
          toast.error("Something went wrong. Please try again.");
        }
      } finally {
        setFormLoading(false);
      }
    },
  });

  useEffect(() => {
    const countryList = Country.getAllCountries();
    setCountries(countryList);

    // Auto-select India when creating a new supplier (not in edit mode)
    if (!id && countryList.length > 0 && !state?.country) {
      const india = countryList.find(
        (c: any) => c.name === "India" || c.isoCode === "IN"
      );
      if (india) {
        formik.setFieldValue("country", india);
        // Load states for India
        const indiaStates = State.getStatesOfCountry(india.isoCode);
        setStates(indiaStates);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    // Load states and cities for edit mode
    if (id && state?.country && countries.length > 0) {
      const selectedCountry = countries.find(
        (c: any) => c?.name === state.country
      );
      if (selectedCountry) {
        formik.setFieldValue("country", selectedCountry);
        const countryStates = State.getStatesOfCountry(selectedCountry.isoCode);
        setStates(countryStates);
        const selectedState = countryStates.find(
          (s: any) => s?.name === state.state
        );
        if (selectedState) {
          formik.setFieldValue("state", selectedState);
          const cities = City.getCitiesOfState(
            selectedCountry.isoCode,
            selectedState.isoCode
          );
          setCities(cities ?? []);
          if (state?.city) {
            const selectedCity = cities.find(
              (c: any) => c?.name === state.city
            );
            if (selectedCity) {
              formik.setFieldValue("city", selectedCity);
            }
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, state?.country, state?.state, state?.city, countries]);

  return (
    <PageContainer>
      <FormHeader
        title={`${id ? "Edit" : "Add"} Supplier`}
        firstLabel="Supplier"
        secondLabel={id ? "Edit Supplier" : "Add Supplier"}
        handleArrowClick={() => navigate("/supplier-list")}
      />

      <LoaderErrorWrapper loading={formLoding}>
        <Form autoComplete="off" onSubmit={formik.handleSubmit} noValidate>
          <Row>
            <Col lg={3} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                Supplier Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="supplier_name"
                placeholder="Enter Supplier Name"
                value={formik.values.supplier_name}
                onChange={(e) => {
                  const value = e.target.value;
                  formik.setFieldValue("supplier_name", value);
                  // if (/^[a-zA-Z\s]*$/g.test(value)) {
                  //     formik.setFieldValue("supplier_name", value);
                  // }
                }}
                onBlur={formik.handleBlur}
                isInvalid={
                  !!formik.errors.supplier_name && formik.touched.supplier_name
                }
                style={commonInputStyle}
                className="form-control-placeholder"
              />
              <Form.Control.Feedback
                type="invalid"
                style={{ color: "#dc3545" }}
              >
                {formik.touched.supplier_name && formik.errors.supplier_name}
              </Form.Control.Feedback>
            </Col>
            <Col lg={3} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                Supplier Code <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="supplier_code"
                placeholder="Enter Supplier Code"
                value={formik.values.supplier_code}
                onChange={(e) => {
                  const value = e.target.value;
                  formik.setFieldValue("supplier_code", value);
                }}
                onBlur={formik.handleBlur}
                isInvalid={
                  !!formik.errors.supplier_code && formik.touched.supplier_code
                }
                style={commonInputStyle}
                className="form-control-placeholder"
              />
              <Form.Control.Feedback
                type="invalid"
                style={{ color: "#dc3545" }}
              >
                {formik.touched.supplier_code && formik.errors.supplier_code}
              </Form.Control.Feedback>
            </Col>
            <Col lg={3} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                Email <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="email"
                placeholder="Enter Email"
                value={formik.values.email}
                onChange={(e) => {
                  const value = e.target.value;
                  formik.setFieldValue("email", value);
                }}
                onBlur={formik.handleBlur}
                isInvalid={!!formik.errors.email && formik.touched.email}
                style={commonInputStyle}
                className="form-control-placeholder"
              />
              <Form.Control.Feedback
                type="invalid"
                style={{ color: "#dc3545" }}
              >
                {formik.touched.email && formik.errors.email}
              </Form.Control.Feedback>
            </Col>
            <Col lg={3} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                Contact No. <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                maxLength={10}
                name="phone_no"
                placeholder="Enter Contact No."
                value={formik.values.phone_no}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, ""); // Only allow digits
                  formik.setFieldValue("phone_no", value);
                }}
                onBlur={formik.handleBlur}
                isInvalid={!!formik.errors.phone_no && formik.touched.phone_no}
                style={commonInputStyle}
                className="form-control-placeholder"
              />
              <Form.Control.Feedback
                type="invalid"
                style={{ color: "#dc3545" }}
              >
                {formik.touched.phone_no && formik.errors.phone_no}
              </Form.Control.Feedback>
            </Col>
          </Row>
          <Row>
            <Col lg={6} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                Address <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="address"
                placeholder="Enter Address"
                value={formik.values.address}
                onChange={(e) => {
                  const value = e.target.value;
                  formik.setFieldValue("address", value);
                }}
                onBlur={formik.handleBlur}
                isInvalid={!!formik.errors.address && formik.touched.address}
                style={commonInputStyle}
                className="form-control-placeholder"
              />
              <Form.Control.Feedback
                type="invalid"
                style={{ color: "#dc3545" }}
              >
                {formik.touched.address && formik.errors.address}
              </Form.Control.Feedback>
            </Col>
            <Col lg={3} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                Country <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                disablePortal
                size="small"
                options={countries || []}
                getOptionLabel={(option: any) =>
                  option ? option?.name : ""
                }
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                value={formik.values.country}
                onChange={(_, val) => {
                  formik.setFieldValue("country", val);
                  formik.setFieldValue("state", null);
                  formik.setFieldValue("city", null);
                  if (val) {
                    const newStates = State.getStatesOfCountry(val?.isoCode);
                    setStates(newStates);
                    setCities([]);
                  }
                }}
                onBlur={() => formik.setFieldTouched("country", true)}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt: any) => opt?.name || ""
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Country"
                    error={
                      formik.touched.country && Boolean(formik.errors.country)
                    }
                    helperText={
                      formik.touched.country &&
                      formik.errors.country ? (
                        <span>
                          {(() => {
                            const error: any = formik.errors.country;
                            if (typeof error === "string") return error as string;
                            if (Array.isArray(error)) return (error[0] || "") as string;
                            return String(error || "") as string;
                          })()}
                        </span>
                      ) : null
                    }
                    fullWidth
                    size="small"
                    autoComplete="off"
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                    }}
                    InputProps={{
                      ...params.InputProps,
                      style: commonInputStyle,
                    }}
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      style: commonLabelStyle,
                    }}
                  />
                )}
              />
            </Col>
            <Col lg={3} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                State <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                disablePortal
                size="small"
                options={states || []}
                getOptionLabel={(option: any) =>
                  option ? option?.name : ""
                }
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                value={formik.values.state}
                onChange={(_, val) => {
                  formik.setFieldValue("state", val);
                  formik.setFieldValue("city", null);
                  if (formik?.values?.country && val) {
                    const newCities = City.getCitiesOfState(
                      formik?.values?.country?.isoCode ?? "",
                      val.isoCode
                    );
                    setCities(newCities || []);
                  }
                }}
                onBlur={() => formik.setFieldTouched("state", true)}
                disabled={!formik.values.country}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt: any) => opt?.name || ""
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      !formik.values.country
                        ? "Select Country First"
                        : "Select State"
                    }
                    error={formik.touched.state && Boolean(formik.errors.state)}
                    helperText={
                      formik.touched.state &&
                      formik.errors.state ? (
                        <span>
                          {(() => {
                            const error: any = formik.errors.state;
                            if (typeof error === "string") return error as string;
                            if (Array.isArray(error)) return (error[0] || "") as string;
                            return String(error || "") as string;
                          })()}
                        </span>
                      ) : null
                    }
                    fullWidth
                    size="small"
                    autoComplete="off"
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                    }}
                    InputProps={{
                      ...params.InputProps,
                      style: commonInputStyle,
                    }}
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      style: commonLabelStyle,
                    }}
                  />
                )}
              />
            </Col>
            <Col lg={3} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                City <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                disablePortal
                size="small"
                options={cities || []}
                getOptionLabel={(option: any) =>
                  option ? option?.name : ""
                }
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                value={formik.values.city}
                onChange={(_, val) => {
                  formik.setFieldValue("city", val);
                }}
                onBlur={() => formik.setFieldTouched("city", true)}
                disabled={!formik.values.state}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt: any) => opt?.name || ""
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      !formik.values.state ? "Select State First" : "Select City"
                    }
                    error={formik.touched.city && Boolean(formik.errors.city)}
                    helperText={
                      formik.touched.city &&
                      formik.errors.city ? (
                        <span>
                          {(() => {
                            const error: any = formik.errors.city;
                            if (typeof error === "string") return error as string;
                            if (Array.isArray(error)) return (error[0] || "") as string;
                            return String(error || "") as string;
                          })()}
                        </span>
                      ) : null
                    }
                    fullWidth
                    size="small"
                    autoComplete="off"
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                    }}
                    InputProps={{
                      ...params.InputProps,
                      style: commonInputStyle,
                    }}
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      style: commonLabelStyle,
                    }}
                  />
                )}
              />
            </Col>
            
            <Col lg={3} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                Pincode <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                maxLength={6}
                name="pincode"
                placeholder="Enter Pincode"
                value={formik.values.pincode}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^[0-9\s]*$/g.test(value)) {
                    formik.setFieldValue("pincode", value);
                  }
                }}
                onBlur={formik.handleBlur}
                isInvalid={!!formik.errors.pincode && formik.touched.pincode}
                style={commonInputStyle}
                className="form-control-placeholder"
              />
              <Form.Control.Feedback
                type="invalid"
                style={{ color: "#dc3545" }}
              >
                {formik.touched.pincode && formik.errors.pincode}
              </Form.Control.Feedback>
            </Col>
            <Col lg={3} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                GSTIN <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="gst_in"
                placeholder="Enter GSTIN"
                value={formik.values.gst_in}
                onChange={(e) => {
                  const value = e.target.value;
                  formik.setFieldValue("gst_in", value);
                }}
                onBlur={formik.handleBlur}
                isInvalid={!!formik.errors.gst_in && formik.touched.gst_in}
                style={commonInputStyle}
                className="form-control-placeholder"
              />
              <Form.Control.Feedback
                type="invalid"
                style={{ color: "#dc3545" }}
              >
                {formik.touched.gst_in && formik.errors.gst_in}
              </Form.Control.Feedback>
            </Col>
            {/* <Col lg={3} className="mb-3">
                                <Form.Label>
                                    Remark
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="remark"
                                    placeholder="Enter Remark"
                                    value={formik.values.remark}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        formik.setFieldValue("remark", value);
                                    }}
                                    onBlur={formik.handleBlur}
                                    isInvalid={!!formik.errors.remark && formik.touched.remark}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.remark}
                                </Form.Control.Feedback>
                            </Col> */}
          </Row>
          <hr />
          <Row>
            <p className="mb-2">
              <strong>Contact Person Details</strong>
            </p>
            <Col lg={3} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                Contact Person Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="contact_person_name"
                placeholder="Enter Contact Person Name"
                value={formik.values.contact_person_name}
                onChange={(e) => {
                  const value = e.target.value;
                  formik.setFieldValue("contact_person_name", value);
                }}
                onBlur={formik.handleBlur}
                isInvalid={
                  !!formik.errors.contact_person_name &&
                  formik.touched.contact_person_name
                }
                style={commonInputStyle}
                className="form-control-placeholder"
              />
              <Form.Control.Feedback
                type="invalid"
                style={{ color: "#dc3545" }}
              >
                {formik.touched.contact_person_name &&
                  formik.errors.contact_person_name}
              </Form.Control.Feedback>
            </Col>
            <Col lg={3} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                Contact Person Mobile No. <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                maxLength={10}
                name="contact_person_phone_no"
                placeholder="Enter Contact Person Mobile No."
                value={formik.values.contact_person_phone_no}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, ""); // Only allow digits
                  formik.setFieldValue("contact_person_phone_no", value);
                }}
                onBlur={formik.handleBlur}
                isInvalid={
                  !!formik.errors.contact_person_phone_no &&
                  formik.touched.contact_person_phone_no
                }
                style={commonInputStyle}
                className="form-control-placeholder"
              />
              <Form.Control.Feedback
                type="invalid"
                style={{ color: "#dc3545" }}
              >
                {formik.touched.contact_person_phone_no &&
                  formik.errors.contact_person_phone_no}
              </Form.Control.Feedback>
            </Col>
          </Row>
          <hr />

          <Row>
            <p className="mb-2">
              <strong>Transporter Details (Optional)</strong>
            </p>

            {/* Transporter 1 */}
            <Col lg={3} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>Transporter Name 1</Form.Label>
              <Form.Control
                type="text"
                name="transporter_details[0].transporter_name"
                placeholder="Enter Name"
                value={formik.values.transporter_details?.[0]?.transporter_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={
                  !!(formik.touched.transporter_details as any)?.[0]
                    ?.transporter_name &&
                  !!(formik.errors.transporter_details as any)?.[0]
                    ?.transporter_name
                }
                style={commonInputStyle}
                className="form-control-placeholder"
              />
              <Form.Control.Feedback
                type="invalid"
                style={{ color: "#dc3545" }}
              >
                {(formik.touched.transporter_details as any)?.[0]
                  ?.transporter_name &&
                  (formik.errors.transporter_details as any)?.[0]
                    ?.transporter_name}
              </Form.Control.Feedback>
            </Col>

            <Col lg={3} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>Contact Person Name 1</Form.Label>
              <Form.Control
                type="text"
                name="transporter_details[0].transporter_contact_person_name"
                placeholder="Enter Contact Person Name"
                value={
                  formik.values.transporter_details?.[0]
                    ?.transporter_contact_person_name
                }
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={
                  !!(formik.touched.transporter_details as any)?.[0]
                    ?.transporter_contact_person_name &&
                  !!(formik.errors.transporter_details as any)?.[0]
                    ?.transporter_contact_person_name
                }
                style={commonInputStyle}
                className="form-control-placeholder"
              />
              <Form.Control.Feedback
                type="invalid"
                style={{ color: "#dc3545" }}
              >
                {(formik.touched.transporter_details as any)?.[0]
                  ?.transporter_contact_person_name &&
                  (formik.errors.transporter_details as any)?.[0]
                    ?.transporter_contact_person_name}
              </Form.Control.Feedback>
            </Col>

            <Col lg={3} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>Contact Person Mobile No.</Form.Label>
              <Form.Control
                type="text"
                maxLength={10}
                name="transporter_details[0].transporter_contact_person_phone_no"
                placeholder="Enter Mobile No."
                value={
                  formik.values.transporter_details?.[0]
                    ?.transporter_contact_person_phone_no
                }
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, "");
                  formik.setFieldValue(
                    "transporter_details[0].transporter_contact_person_phone_no",
                    value
                  );
                }}
                onBlur={formik.handleBlur}
                isInvalid={
                  !!(formik.touched.transporter_details as any)?.[0]
                    ?.transporter_contact_person_phone_no &&
                  !!(formik.errors.transporter_details as any)?.[0]
                    ?.transporter_contact_person_phone_no
                }
                style={commonInputStyle}
                className="form-control-placeholder"
              />
              <Form.Control.Feedback
                type="invalid"
                style={{ color: "#dc3545" }}
              >
                {(formik.touched.transporter_details as any)?.[0]
                  ?.transporter_contact_person_phone_no &&
                  (formik.errors.transporter_details as any)?.[0]
                    ?.transporter_contact_person_phone_no}
              </Form.Control.Feedback>
            </Col>
            <br />
            {/* Transporter 2 */}
            <Col lg={3} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>Transporter Name 2</Form.Label>
              <Form.Control
                type="text"
                name="transporter_details[1].transporter_name"
                placeholder="Enter Name"
                value={formik.values.transporter_details?.[1]?.transporter_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={
                  !!(formik.touched.transporter_details as any)?.[1]
                    ?.transporter_name &&
                  !!(formik.errors.transporter_details as any)?.[1]
                    ?.transporter_name
                }
                style={commonInputStyle}
                className="form-control-placeholder"
              />
              <Form.Control.Feedback
                type="invalid"
                style={{ color: "#dc3545" }}
              >
                {(formik.touched.transporter_details as any)?.[1]
                  ?.transporter_name &&
                  (formik.errors.transporter_details as any)?.[1]
                    ?.transporter_name}
              </Form.Control.Feedback>
            </Col>

            <Col lg={3} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>Contact Person Name 2</Form.Label>
              <Form.Control
                type="text"
                name="transporter_details[1].transporter_contact_person_name"
                placeholder="Enter Contact Person Name"
                value={
                  formik.values.transporter_details?.[1]
                    ?.transporter_contact_person_name
                }
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={
                  !!(formik.touched.transporter_details as any)?.[1]
                    ?.transporter_contact_person_name &&
                  !!(formik.errors.transporter_details as any)?.[1]
                    ?.transporter_contact_person_name
                }
                style={commonInputStyle}
                className="form-control-placeholder"
              />
              <Form.Control.Feedback
                type="invalid"
                style={{ color: "#dc3545" }}
              >
                {(formik.touched.transporter_details as any)?.[1]
                  ?.transporter_contact_person_name &&
                  (formik.errors.transporter_details as any)?.[1]
                    ?.transporter_contact_person_name}
              </Form.Control.Feedback>
            </Col>

            <Col lg={3} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>Contact Person Mobile No. 2</Form.Label>
              <Form.Control
                type="text"
                maxLength={10}
                name="transporter_details[1].transporter_contact_person_phone_no"
                placeholder="Enter Mobile No."
                value={
                  formik.values.transporter_details?.[1]
                    ?.transporter_contact_person_phone_no
                }
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, "");
                  formik.setFieldValue(
                    "transporter_details[1].transporter_contact_person_phone_no",
                    value
                  );
                }}
                onBlur={formik.handleBlur}
                isInvalid={
                  !!(formik.touched.transporter_details as any)?.[1]
                    ?.transporter_contact_person_phone_no &&
                  !!(formik.errors.transporter_details as any)?.[1]
                    ?.transporter_contact_person_phone_no
                }
                style={commonInputStyle}
                className="form-control-placeholder"
              />
              <Form.Control.Feedback
                type="invalid"
                style={{ color: "#dc3545" }}
              >
                {(formik.touched.transporter_details as any)?.[1]
                  ?.transporter_contact_person_phone_no &&
                  (formik.errors.transporter_details as any)?.[1]
                    ?.transporter_contact_person_phone_no}
              </Form.Control.Feedback>
            </Col>
          </Row>

          <div className="d-flex justify-content-center gap-3 py-3">
            {/* <Button
              variant="light"
              onClick={() => navigate(-1)}
              style={{
                backgroundColor: "white",
                border: "1px solid #ced4da",
                color: "#212529",
              }}
            >
              Cancel
            </Button> */}
            <CancelBackButton to="/supplier-list" />

            {/* Reusable submit button with spinner */}
            <SubmitButton isSubmitting={formik.isSubmitting} isEdit={!!id} />
          </div>
        </Form>
      </LoaderErrorWrapper>
    </PageContainer>
  );
};

export default SupplierMasterForm;
